/** JHN Meet — visioconférence P2P à deux (WebRTC : caméra, micro, partage d'écran). */
import { el, clear } from '../ui/dom.js';
import { icon } from '../ui/icons.js';
import { toast } from '../ui/toast.js';
import { navigate } from '../router.js';

const ICE = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }] };
const rid = () => Math.random().toString(36).slice(2, 9);

export function renderMeet(ctx) {
  ctx.setCrumbs([{ label: 'Meet' }]);
  const room = ctx.params && ctx.params.room;
  return room ? call(ctx, room) : lobby(ctx);
}

// ── Accueil : créer / rejoindre ──
function lobby(ctx) {
  const start = el('button', { class: 'btn btn-primary', style: { height: '52px', padding: '0 26px', fontSize: '15px' }, html: icon('meet', 20) + 'Nouvelle réunion' });
  start.addEventListener('click', () => navigate('/meet/' + rid()));
  ctx.content.appendChild(el('div', { class: 'meet-soon' },
    el('div', { class: 'glyph', style: { width: '74px', height: '74px', borderRadius: '20px', display: 'grid', placeItems: 'center', background: '#06b6d4', color: '#fff' }, html: icon('meet', 34) }),
    el('h2', { style: { fontSize: '26px' } }, 'JHN Meet'),
    el('p', { style: { color: 'var(--text-dim)', maxWidth: '440px', lineHeight: 1.5 } }, 'Visioconférence à deux : caméra, micro et partage d’écran. Créez une réunion puis partagez le lien à votre interlocuteur.'),
    start));
}

// ── Appel ──
function call(ctx, room) {
  const { content } = ctx;
  const socket = ctx.shell.getChat();
  let pc = null, localStream = null, camTrack = null, screenStream = null, destroyed = false;
  let micOn = true, camOn = true, sharing = false;
  const pending = [];

  content.style.padding = '0';
  const remoteVideo = el('video', { autoplay: '', playsinline: '' });
  const localVideo = el('video', { autoplay: '', playsinline: '', muted: '' }); localVideo.muted = true; localVideo.style.transform = 'scaleX(-1)';
  const statusEl = el('div', { class: 'meet-status' }, 'Connexion à la caméra…');
  const stage = el('div', { class: 'meet-stage' }, remoteVideo, statusEl, el('div', { class: 'meet-pip' }, localVideo));

  const bar = el('div', { class: 'meet-bar' });
  const micBtn = ctrl('mic', 'Micro', () => toggleMic());
  const camBtn = ctrl('meet', 'Caméra', () => toggleCam());
  const screenBtn = ctrl('screen', 'Partager l’écran', () => toggleScreen());
  const copyBtn = ctrl('copy', 'Copier le lien', () => { navigator.clipboard?.writeText(location.href).then(() => toast('Lien copié')); });
  const hangBtn = ctrl('hangup', 'Quitter', () => { cleanup(); navigate('/meet'); }, 'meet-hang');
  bar.append(micBtn, camBtn, screenBtn, copyBtn, hangBtn);
  content.append(el('div', { class: 'meet' }, stage, bar));

  function ctrl(ic, title, onClick, extra) {
    const b = el('button', { class: 'meet-ctrl' + (extra ? ' ' + extra : ''), title, html: icon(ic, 22) });
    b.addEventListener('click', onClick);
    return b;
  }

  // ── Média local ──
  (async () => {
    try {
      localStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: true });
    } catch {
      statusEl.textContent = 'Caméra/micro indisponibles ou refusés.';
      return;
    }
    if (destroyed) { localStream.getTracks().forEach((t) => t.stop()); return; }
    camTrack = localStream.getVideoTracks()[0];
    localVideo.srcObject = localStream;
    localVideo.play().catch(() => {});
    join();
  })();

  // ── Signalisation ──
  const offJoined = socket.on('meet-peer-joined', (m) => { if (m.room === room) makeCall(true); });
  const offPresent = socket.on('meet-peer-present', (m) => { if (m.room === room) makeCall(false); statusEl.textContent = 'Connexion…'; });
  const offSignal = socket.on('meet-signal', (m) => { if (m.room === room) onSignal(m.signal); });
  const offLeft = socket.on('meet-peer-left', (m) => { if (m.room === room) peerGone(); });
  const offFull = socket.on('meet-full', (m) => { if (m.room === room) { statusEl.textContent = 'La réunion est déjà complète (2 personnes max).'; } });

  function join() { socket.send({ t: 'meet-join', room }); statusEl.textContent = 'En attente de l’autre personne…'; }

  function ensurePc() {
    if (pc) return pc;
    pc = new RTCPeerConnection(ICE);
    localStream.getTracks().forEach((t) => pc.addTrack(t, localStream));
    pc.onicecandidate = (e) => { if (e.candidate) socket.send({ t: 'meet-signal', room, signal: { candidate: e.candidate } }); };
    pc.ontrack = (e) => { remoteVideo.srcObject = e.streams[0]; statusEl.style.display = 'none'; };
    pc.onconnectionstatechange = () => {
      if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) { statusEl.style.display = ''; statusEl.textContent = 'Connexion perdue.'; }
    };
    return pc;
  }

  async function makeCall(initiator) {
    ensurePc();
    if (!initiator) return; // l'autre enverra l'offre
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.send({ t: 'meet-signal', room, signal: { sdp: pc.localDescription } });
    } catch (e) { console.error(e); }
  }

  async function onSignal(signal) {
    ensurePc();
    try {
      if (signal.sdp) {
        await pc.setRemoteDescription(signal.sdp);
        for (const c of pending.splice(0)) { try { await pc.addIceCandidate(c); } catch {} }
        if (signal.sdp.type === 'offer') {
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          socket.send({ t: 'meet-signal', room, signal: { sdp: pc.localDescription } });
        }
      } else if (signal.candidate) {
        if (pc.remoteDescription && pc.remoteDescription.type) { try { await pc.addIceCandidate(signal.candidate); } catch {} }
        else pending.push(signal.candidate);
      }
    } catch (e) { console.error(e); }
  }

  function peerGone() {
    remoteVideo.srcObject = null;
    if (pc) { try { pc.close(); } catch {} pc = null; }
    statusEl.style.display = ''; statusEl.textContent = 'Votre interlocuteur a quitté la réunion.';
  }

  // ── Contrôles ──
  function toggleMic() { micOn = !micOn; localStream.getAudioTracks().forEach((t) => (t.enabled = micOn)); micBtn.classList.toggle('off', !micOn); micBtn.innerHTML = icon(micOn ? 'mic' : 'micOff', 22); }
  function toggleCam() { camOn = !camOn; localStream.getVideoTracks().forEach((t) => (t.enabled = camOn)); camBtn.classList.toggle('off', !camOn); camBtn.innerHTML = icon(camOn ? 'meet' : 'videoOff', 22); }
  async function toggleScreen() {
    if (sharing) { stopScreen(); return; }
    try { screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true }); }
    catch { return; }
    const track = screenStream.getVideoTracks()[0];
    sharing = true; screenBtn.classList.add('on');
    const sender = pc && pc.getSenders().find((s) => s.track && s.track.kind === 'video');
    if (sender) sender.replaceTrack(track);
    localVideo.srcObject = screenStream; localVideo.style.transform = 'none';
    track.onended = () => stopScreen();
  }
  function stopScreen() {
    if (!sharing) return;
    sharing = false; screenBtn.classList.remove('on');
    if (screenStream) screenStream.getTracks().forEach((t) => t.stop());
    const sender = pc && pc.getSenders().find((s) => s.track && s.track.kind === 'video');
    if (sender && camTrack) sender.replaceTrack(camTrack);
    localVideo.srcObject = localStream; localVideo.style.transform = 'scaleX(-1)';
  }

  function cleanup() {
    destroyed = true;
    offJoined(); offPresent(); offSignal(); offLeft(); offFull();
    try { socket.send({ t: 'meet-leave', room }); } catch {}
    if (pc) { try { pc.close(); } catch {} }
    if (localStream) localStream.getTracks().forEach((t) => t.stop());
    if (screenStream) screenStream.getTracks().forEach((t) => t.stop());
    content.style.padding = '';
  }
  return cleanup;
}
